/**
 * src/hooks/useBroadcastRecorder.ts
 *
 * `MediaRecorder` lifecycle wrapper for the broadcaster's local
 * stream.  Handles three concerns the rest of ProfileOverlay
 * shouldn't have to think about:
 *
 *   1. Picking a codec the broadcaster's browser actually supports.
 *      We try VP9 + Opus first (smaller files), fall back to VP8 +
 *      Opus, then to whatever default the platform wants — Safari
 *      lands on H.264/MP4 in a WebM container, which the server
 *      stores as-is and serves back the same way.
 *   2. Buffering chunks safely.  `MediaRecorder` fires `dataavailable`
 *      every `timeslice` ms; we collect blobs in a ref so the React
 *      tree doesn't churn.  On stop we concatenate them into a
 *      single Blob the upload route can consume.
 *   3. Capturing tool + gift events with relative timestamps.  The
 *      hook subscribes to the existing event buses while the recorder
 *      is running; consumers retrieve the captured timeline alongside
 *      the blob and post it to the upload endpoint so playback can
 *      replay overlays in sync with the video.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  subscribeToLiveToolEvents, type LiveToolEvent,
} from '../services/liveToolsService';
import type { RecordedEvent } from '../services/pastBroadcastsService';
import type { LiveGiftEvent } from '../services/webrtcBroadcastService';

const PREFERRED_MIME_TYPES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  '', // let the browser pick
];

const DEFAULT_TIMESLICE_MS = 5000; // emit a chunk every 5s
const MAX_RECORDING_BYTES = 480 * 1024 * 1024; // ~480 MB ceiling client-side

/**
 * Default video/audio bitrates we ask MediaRecorder for when the
 * caller didn't override them.  Modern browsers default to roughly
 * 2.5 Mbps (video) + 128 Kbps (audio) which is great for an offline
 * encode but quickly produces files large enough to time-out a
 * default Cloudflare-fronted upload (~100s window).  A webcam-class
 * 800 Kbps + 64 Kbps stream still looks fine for replay and lands a
 * 5-minute broadcast in ~32 MB instead of ~100 MB.
 */
const DEFAULT_VIDEO_BPS = 800_000;
const DEFAULT_AUDIO_BPS = 64_000;

/** Public state surfaced to the React tree. */
export interface BroadcastRecorderState {
  recording: boolean;
  startedAt: string | null;
  /** Total bytes buffered locally so far — used for a soft warning UI. */
  bufferedBytes: number;
  error: string | null;
}

/** Result returned by stop(). */
export interface BroadcastRecorderResult {
  blob: Blob;
  startedAt: string;
  endedAt: string;
  durationSec: number;
  events: RecordedEvent[];
}

interface UseBroadcastRecorderOpts {
  /**
   * Pulls the broadcaster's local stream when needed.  Using a
   * getter (rather than passing the stream directly) means the hook
   * doesn't fight with the camera-flip flow that swaps streams
   * mid-broadcast — start() always grabs whatever is current.
   */
  getStream: () => MediaStream | null;
  /** Optional bitrate override in bps. */
  videoBitsPerSecond?: number;
  audioBitsPerSecond?: number;
}

export function useBroadcastRecorder(opts: UseBroadcastRecorderOpts) {
  const [state, setState] = useState<BroadcastRecorderState>({
    recording: false,
    startedAt: null,
    bufferedBytes: 0,
    error: null,
  });

  const recorderRef    = useRef<MediaRecorder | null>(null);
  const chunksRef      = useRef<Blob[]>([]);
  const eventsRef      = useRef<RecordedEvent[]>([]);
  const startedAtRef   = useRef<string | null>(null);
  const startedAtMsRef = useRef<number>(0);
  const liveToolUnsubRef = useRef<null | (() => void)>(null);

  // Keep a stable reference to the latest options so callbacks can
  // be memoised once and still pick up changes.
  const optsRef = useRef(opts);
  useEffect(() => { optsRef.current = opts; }, [opts]);

  const _resetState = useCallback(() => {
    chunksRef.current = [];
    eventsRef.current = [];
    startedAtRef.current = null;
    startedAtMsRef.current = 0;
    setState({ recording: false, startedAt: null, bufferedBytes: 0, error: null });
  }, []);

  const _captureToolEvent = useCallback((ev: LiveToolEvent) => {
    if (!recorderRef.current) return;
    const relativeMs = Math.max(0, Date.now() - startedAtMsRef.current);
    eventsRef.current.push({ relativeMs, kind: 'tool', payload: ev });
  }, []);

  /**
   * External hook for the gift-toast pipeline — the broadcaster
   * tree already dispatches `LiveGiftEvent`s for the toast layer;
   * we expose a setter so the parent can forward them to the
   * recorder while it's running.
   */
  const captureGiftEvent = useCallback((gift: LiveGiftEvent) => {
    if (!recorderRef.current) return;
    const relativeMs = Math.max(0, Date.now() - startedAtMsRef.current);
    eventsRef.current.push({ relativeMs, kind: 'gift', payload: gift });
  }, []);

  const start = useCallback((): boolean => {
    if (recorderRef.current) return true; // already running
    const stream = optsRef.current.getStream();
    if (!stream) {
      setState((p) => ({ ...p, error: 'no_stream' }));
      return false;
    }
    let mimeType = '';
    for (const candidate of PREFERRED_MIME_TYPES) {
      if (!candidate) { mimeType = ''; break; }
      try {
        if ((MediaRecorder as any).isTypeSupported?.(candidate)) {
          mimeType = candidate;
          break;
        }
      } catch { /* keep iterating */ }
    }
    const videoBitsPerSecond = optsRef.current.videoBitsPerSecond ?? DEFAULT_VIDEO_BPS;
    const audioBitsPerSecond = optsRef.current.audioBitsPerSecond ?? DEFAULT_AUDIO_BPS;
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, mimeType ? {
        mimeType,
        videoBitsPerSecond,
        audioBitsPerSecond,
      } : { videoBitsPerSecond, audioBitsPerSecond });
    } catch (err: any) {
      // Some browsers (older Safari) reject explicit bitrate hints
      // even when they support the codec.  Retry once without them
      // before giving up so the recording still starts at the
      // browser's own default bitrate.
      try {
        recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      } catch (retryErr: any) {
        setState((p) => ({ ...p, error: retryErr?.message || err?.message || 'mediarecorder_unsupported' }));
        return false;
      }
    }

    chunksRef.current = [];
    eventsRef.current = [];
    let totalBytes = 0;
    recorder.ondataavailable = (ev) => {
      if (!ev.data || ev.data.size === 0) return;
      // Soft cap — once we exceed the ceiling we stop accepting new
      // chunks.  The recorder keeps running until the broadcaster
      // ends the live; the file is just truncated to the cap.
      if (totalBytes >= MAX_RECORDING_BYTES) return;
      chunksRef.current.push(ev.data);
      totalBytes += ev.data.size;
      setState((p) => ({ ...p, bufferedBytes: totalBytes }));
    };
    recorder.onerror = (ev: any) => {
      setState((p) => ({ ...p, error: ev?.error?.name || 'recorder_error' }));
    };

    recorderRef.current = recorder;
    startedAtRef.current = new Date().toISOString();
    startedAtMsRef.current = Date.now();

    // Subscribe to tool events so we can replay them later.
    liveToolUnsubRef.current = subscribeToLiveToolEvents(_captureToolEvent);

    try {
      recorder.start(DEFAULT_TIMESLICE_MS);
    } catch (err: any) {
      recorderRef.current = null;
      setState((p) => ({ ...p, error: err?.message || 'recorder_start_failed' }));
      return false;
    }

    setState({
      recording: true,
      startedAt: startedAtRef.current,
      bufferedBytes: 0,
      error: null,
    });
    return true;
  }, [_captureToolEvent]);

  const stop = useCallback((): Promise<BroadcastRecorderResult | null> => {
    return new Promise((resolve) => {
      const recorder = recorderRef.current;
      if (!recorder) { resolve(null); return; }

      const finalise = () => {
        const startedAt = startedAtRef.current || new Date().toISOString();
        const startedMs = startedAtMsRef.current || Date.now();
        const endedAtMs = Date.now();
        const durationSec = Math.max(0, Math.round((endedAtMs - startedMs) / 1000));
        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || 'video/webm',
        });
        const events = eventsRef.current.slice();

        recorderRef.current = null;
        if (liveToolUnsubRef.current) {
          try { liveToolUnsubRef.current(); } catch { /* ignore */ }
          liveToolUnsubRef.current = null;
        }
        _resetState();
        resolve({
          blob,
          startedAt,
          endedAt: new Date(endedAtMs).toISOString(),
          durationSec,
          events,
        });
      };

      recorder.onstop = finalise;
      try {
        recorder.stop();
      } catch {
        // If stop() throws (e.g. recorder already inactive), still
        // surface whatever we have buffered so the caller isn't
        // left holding nothing.
        finalise();
      }
    });
  }, [_resetState]);

  /** Cancel without producing a blob — used when the broadcast
   *  ends with an error and we don't want to upload garbage. */
  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    recorder.onstop = null;
    try { recorder.stop(); } catch { /* ignore */ }
    recorderRef.current = null;
    if (liveToolUnsubRef.current) {
      try { liveToolUnsubRef.current(); } catch { /* ignore */ }
      liveToolUnsubRef.current = null;
    }
    _resetState();
  }, [_resetState]);

  // Tear down on unmount so we never leave a recorder running with
  // a stream the parent component has already torn down.
  useEffect(() => () => cancel(), [cancel]);

  return { state, start, stop, cancel, captureGiftEvent };
}
